import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { ArrowDown, ArrowUp, Zap, Clock, Users, Info, ChevronRight, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import ActiveFiltersChips from "@/components/ActiveFiltersChips";
import DistributionScopeBanner from "@/components/DistributionScopeBanner";
import HoursSourceBadge from "@/components/HoursSourceBadge";
import { useFilters } from "@/hooks/useFilters";
import reports from "@/api/reports";
import { FALLBACK_COLORS, TX_COLORS, colorFor } from "@/lib/reportColors";
import HelpTooltip from "@/components/HelpTooltip";

// Phase 9.5 — Time Clock consolidation. TE350 is the only source visible in
// the UI (Phase 9.8: AUTO was removed by client request). Legacy MONTH /
// YEARLY / AUTO selections still work through the backend for backward
// compatibility of shared URLs and API scripts. `SOURCE_LABEL` keeps every
// entry so historical data queried via the API still renders a friendly
// context label.
const SOURCE_OPTIONS = [
  { value: "TE350", label: "Time Clock (TE350)",     hint: "Fuente unificada de horas del ERP" },
];
const HOURS_SOURCE_OPTS = SOURCE_OPTIONS;
const SOURCE_LABEL = {
  AUTO: "Auto (prioridad)",
  TE350: "Time Clock (TE350)",
  MONTH: "Payroll (MONTH) — legacy",
  YEARLY: "Anual (YEARLY) — legacy",
};

function KpiCard({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <Card data-testid={testId} className="overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg">
      <CardContent className="relative p-5">
        <div
          className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-10"
          style={{ background: accent }}
        />
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="mt-1 text-3xl font-semibold tabular-nums">{value}</div>
            {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
          </div>
          <div
            className="flex h-10 w-10 items-center justify-center rounded-md"
            style={{ background: `${accent}22`, color: accent }}
          >
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Sparkbar showing tx/hr as a horizontal proportion bar.
function Sparkbar({ value, max }) {
  if (value == null || max <= 0) {
    return <div className="h-2 w-full max-w-[120px] rounded-full bg-muted/40" />;
  }
  const pct = Math.min(100, Math.max(2, (value / max) * 100));
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 rounded-full bg-muted/40">
        <div
          className="h-2 rounded-full"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, #9ECE6A 0%, #E0AF68 60%, #F7768E 100%)`,
          }}
        />
      </div>
      <span className="tabular-nums">{value.toFixed(2)}</span>
    </div>
  );
}

const SORT_OPTIONS = [
  { value: "tx_per_hour", label: "filters.sort.tx_per_hour" },
  { value: "transactions", label: "filters.sort.transactions" },
  { value: "hours", label: "filters.sort.hours" },
];

// Phase 9.3 — refined anomaly thresholds. Returns null if the row is normal.
function classifyAnomaly(r) {
  const tph = r.tx_per_hour;
  if (tph == null) return null;
  if (tph > 200) {
    return {
      label: "posible error de horas",
      tooltip:
        "tx/hr > 200 — muy probablemente las horas están gravemente incompletas o el empleado tiene tx atribuidas erróneamente.",
      className: "border-red-500/60 bg-red-500/10 text-red-500",
    };
  }
  if (tph > 100) {
    return {
      label: "horas incompletas?",
      tooltip:
        "tx/hr entre 100 y 200 — puede indicar horas parciales para el rango seleccionado.",
      className: "border-amber-500/50 text-amber-500",
    };
  }
  if (tph < 5 && r.total_transactions > 20) {
    return {
      label: "horas sobre-registradas?",
      tooltip:
        "tx/hr < 5 pero con más de 20 tx — posible caso donde el time clock cuenta más horas que la realidad operativa.",
      className: "border-blue-500/50 text-blue-500",
    };
  }
  return null;
}

function DeltaBadge({ value }) {
  if (value == null) return <span className="text-xs text-muted-foreground">—</span>;
  const sign = value >= 0 ? "+" : "";
  const isPos = value >= 0;
  const Icon = isPos ? ArrowUp : ArrowDown;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums ${
        isPos
          ? "bg-emerald-500/10 text-emerald-500"
          : "bg-rose-500/10 text-rose-500"
      }`}
    >
      <Icon className="h-3 w-3" />
      {sign}
      {value.toFixed(1)}
    </span>
  );
}

// Renders a horizontal-bar breakdown of tx types for one employee.
function TxTypeBreakdown({ byType, total }) {
  const entries = Object.entries(byType || {})
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) {
    return (
      <div className="text-xs text-muted-foreground">
        Sin desglose disponible.
      </div>
    );
  }
  return (
    <div className="space-y-1.5 pl-8 pr-4" data-testid="tx-breakdown">
      <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
        Desglose por tipo de transacción
      </div>
      {entries.map(([type, count], i) => {
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={type} className="flex items-center gap-3 text-xs">
            <div className="flex w-24 items-center gap-2 shrink-0">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: TX_COLORS[type] || colorFor(type, i) }}
              />
              <span className="font-mono">{type}</span>
            </div>
            <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted/40">
              <div
                className="absolute inset-y-0 left-0 rounded-full"
                style={{
                  width: `${pct}%`,
                  background: TX_COLORS[type] || colorFor(type, i),
                }}
              />
            </div>
            <div className="w-32 text-right font-mono tabular-nums text-muted-foreground">
              {count.toLocaleString()}{" "}
              <span className="text-foreground">({pct.toFixed(1)}%)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Efficiency() {
  const { t } = useTranslation();
  const { filters, setFilters, reset, payload } = useFilters();
  const [sortBy, setSortBy] = useState("tx_per_hour");
  const [hoursSource, setHoursSource] = useState("TE350");
  const [data, setData] = useState({
    items: [],
    avg_tx_per_hour: null,
    median_tx_per_hour: null,
    total_hours: 0,
    with_hours: 0,
    without_hours: 0,
    without_tx: 0,
  });
  const [loading, setLoading] = useState(false);
  // Bumped from the banner's onChange to force a refetch after clearing scope.
  const [scopeNonce, setScopeNonce] = useState(0);
  // Phase 9.3 — expanded row for tx-type breakdown.
  const [expandedRow, setExpandedRow] = useState(null);

  // Stringify the filter payload so referential changes don't trigger needless
  // refetches (intentional — payload identity changes on every render).
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    reports
      .efficiency(payload, sortBy, hoursSource)
      .then((r) => alive && setData(r.data || { items: [] }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [payloadKey, sortBy, hoursSource, scopeNonce]);

  const top = useMemo(
    () =>
      data.items.find((r) => r.tx_per_hour != null && r.tx_per_hour > 0) || null,
    [data]
  );
  const maxTxPerHour = useMemo(
    () => Math.max(...data.items.map((r) => r.tx_per_hour || 0), 1),
    [data]
  );
  const scatterPoints = useMemo(
    () =>
      data.items
        .filter((r) => r.total_hours > 0 && r.total_transactions > 0)
        .map((r) => ({
          x: r.total_hours,
          y: r.total_transactions,
          z: r.tx_per_hour || 0,
          name: r.canonical_name,
        })),
    [data]
  );

  return (
    <div className="space-y-6" data-testid="efficiency-page">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight" data-testid="efficiency-title">
          <Zap className="h-7 w-7 text-yellow-500" />
          {t("efficiency.title")}
          <HelpTooltip anchor="efficiency" label={t("help.tooltip.efficiency")} testId="help-tooltip-efficiency" />
        </h1>
        <p className="text-sm text-muted-foreground">{t("efficiency.subtitle")}</p>
      </div>

      <GlobalFilters filters={filters} onChange={setFilters} onReset={reset} />

      <ActiveFiltersChips
        filters={filters}
        onClear={(key, next) => {
          if (key === "__all__") {
            setFilters({ employee_ids: [], department_ids: [], transaction_types: [] });
            return;
          }
          setFilters({ [key]: next });
        }}
        testId="efficiency-drilldown-chips"
      />

      <DistributionScopeBanner
        scope={data.distribution_scope}
        onChange={() => setScopeNonce((n) => n + 1)}
      />

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-3" data-testid="efficiency-kpis">
        <KpiCard
          icon={Zap}
          accent="#E0AF68"
          label={t("efficiency.avgTxHr")}
          value={data.avg_tx_per_hour != null ? data.avg_tx_per_hour.toFixed(2) : "—"}
          testId="eff-kpi-avg"
        />
        <KpiCard
          icon={Users}
          accent="#9ECE6A"
          label={t("efficiency.topEfficient")}
          value={top?.canonical_name || "—"}
          sub={top?.tx_per_hour ? `${top.tx_per_hour} tx/hr` : ""}
          testId="eff-kpi-top"
        />
        <KpiCard
          icon={Clock}
          accent="#7DCFFF"
          label={t("efficiency.totalHours")}
          value={data.total_hours.toLocaleString()}
          sub={`${data.with_hours} ${t("common.employee").toLowerCase()}s`}
          testId="eff-kpi-hours"
        />
      </div>

      {/* Scatter quadrants */}
      <Card data-testid="efficiency-scatter">
        <CardHeader>
          <CardTitle className="text-base">{t("efficiency.scatterTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          {scatterPoints.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              {loading ? t("common.loading") : t("common.no_data")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <ScatterChart margin={{ left: 8, right: 24, top: 12 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis
                  type="number"
                  dataKey="x"
                  name={t("efficiency.hours")}
                  tick={{ fontSize: 11 }}
                  label={{ value: t("efficiency.hours"), position: "insideBottom", offset: -2 }}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  name={t("common.total")}
                  tick={{ fontSize: 11 }}
                  label={{ value: t("efficiency.transactions"), angle: -90, position: "insideLeft" }}
                />
                <ZAxis type="number" dataKey="z" range={[60, 400]} name="tx/hr" />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }}
                  formatter={(v, n, p) => {
                    if (n === "x") return [`${v} hr`, t("efficiency.hours")];
                    if (n === "y") return [`${v} tx`, t("efficiency.transactions")];
                    if (n === "z") return [v.toFixed(2), "tx/hr"];
                    return [v, n];
                  }}
                  labelFormatter={() => ""}
                  cursor={{ strokeDasharray: "3 3" }}
                />
                <Scatter data={scatterPoints} fillOpacity={0.7}>
                  {scatterPoints.map((p, i) => (
                    <Cell key={i} fill={FALLBACK_COLORS[i % FALLBACK_COLORS.length]} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Sortable table */}
      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <CardTitle className="text-base">
                {t("efficiency.tableTitle")} · {data.items.length} {t("common.employee").toLowerCase()}s
              </CardTitle>
              {/* Phase 9.2 — context sub-header: window + active source. */}
              <div
                className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground"
                data-testid="efficiency-context"
              >
                <Info className="h-3 w-3 shrink-0" />
                <span>
                  Horas:{" "}
                  <span className="font-mono">
                    {data.applied_filters?.date_from || "sin límite"}
                    {" → "}
                    {data.applied_filters?.date_to || "sin límite"}
                  </span>
                </span>
                <span aria-hidden="true">·</span>
                <span>
                  Fuente:{" "}
                  <span className="font-medium text-foreground">
                    {SOURCE_LABEL[data.hours_source || hoursSource] || hoursSource}
                  </span>
                </span>
                {data.median_tx_per_hour != null && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span data-testid="efficiency-median">
                      Mediana:{" "}
                      <span className="font-semibold text-foreground tabular-nums">
                        {data.median_tx_per_hour.toFixed(2)}
                      </span>{" "}
                      tx/hr
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="hours-source-select"
                className="hidden text-xs uppercase tracking-wide text-muted-foreground sm:inline"
              >
                Fuente de horas
              </label>
              <Select value={hoursSource} onValueChange={setHoursSource}>
                <SelectTrigger
                  id="hours-source-select"
                  className="h-9 w-[200px]"
                  data-testid="hours-source-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  {HOURS_SOURCE_OPTS.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      data-testid={`hours-source-opt-${o.value}`}
                    >
                      <div className="flex flex-col">
                        <span>{o.label}</span>
                        <span className="text-[10px] text-muted-foreground">{o.hint}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="efficiency-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8 text-xs"></TableHead>
                  <TableHead className="w-12 text-xs">#</TableHead>
                  <TableHead className="text-xs">{t("common.employee")}</TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-xs"
                    onClick={() => setSortBy("transactions")}
                    data-testid="eff-sort-tx"
                  >
                    <span className="inline-flex items-center gap-1">
                      {t("efficiency.transactions")}
                      {sortBy === "transactions" && <ArrowDown className="h-3 w-3" />}
                    </span>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-xs"
                    onClick={() => setSortBy("hours")}
                    data-testid="eff-sort-hours"
                  >
                    <span className="inline-flex items-center gap-1">
                      {t("efficiency.hours")}
                      {sortBy === "hours" && <ArrowDown className="h-3 w-3" />}
                    </span>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none text-xs"
                    onClick={() => setSortBy("tx_per_hour")}
                    data-testid="eff-sort-tphr"
                  >
                    <span className="inline-flex items-center gap-1">
                      tx/hr {sortBy === "tx_per_hour" && <ArrowDown className="h-3 w-3" />}
                    </span>
                  </TableHead>
                  <TableHead className="text-xs text-right" data-testid="eff-col-delta">
                    Δ vs mediana
                  </TableHead>
                  <TableHead className="text-xs">{t("common.department")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                      {loading ? t("common.loading") : t("common.no_data")}
                    </TableCell>
                  </TableRow>
                ) : (
                  data.items.map((r) => {
                    const rowKey = r.canonical_id || r.canonical_name;
                    const isExpanded = expandedRow === rowKey;
                    const anomaly = classifyAnomaly(r);
                    const delta =
                      r.tx_per_hour != null && data.median_tx_per_hour != null
                        ? r.tx_per_hour - data.median_tx_per_hour
                        : null;
                    return (
                      <React.Fragment key={rowKey}>
                        <TableRow
                          data-testid={`eff-row-${rowKey}`}
                          className={`cursor-pointer transition-colors hover:bg-muted/40 ${
                            isExpanded ? "bg-muted/30" : ""
                          }`}
                          onClick={() => setExpandedRow(isExpanded ? null : rowKey)}
                        >
                          <TableCell className="px-2">
                            <ChevronRight
                              className={`h-4 w-4 text-muted-foreground transition-transform ${
                                isExpanded ? "rotate-90" : ""
                              }`}
                              data-testid={`eff-expand-${rowKey}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {r.rank}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{r.canonical_name}</div>
                            {r.total_hours === 0 && r.total_transactions > 0 && (
                              <Badge variant="secondary" className="mt-1 text-[10px]">
                                {t("efficiency.noHours")}
                              </Badge>
                            )}
                            {r.total_transactions === 0 && r.total_hours > 0 && (
                              <Badge variant="secondary" className="mt-1 text-[10px]">
                                {t("efficiency.noTx")}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {r.total_transactions.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            <div className="flex items-center justify-end gap-1.5">
                              <span>{r.total_hours.toFixed(2)}</span>
                              <HoursSourceBadge
                                source={r.hours_source}
                                detail={r.hours_source_detail}
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            {r.tx_per_hour != null ? (
                              <div className="flex items-center gap-1.5">
                                <Sparkbar value={r.tx_per_hour} max={maxTxPerHour} />
                                {anomaly && (
                                  <Badge
                                    variant="outline"
                                    className={`text-[9px] uppercase ${anomaly.className}`}
                                    title={anomaly.tooltip}
                                    data-testid={`eff-warning-${rowKey}`}
                                  >
                                    <AlertTriangle className="mr-1 h-2.5 w-2.5" />
                                    {anomaly.label}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell
                            className="text-right tabular-nums"
                            data-testid={`eff-delta-${rowKey}`}
                          >
                            <DeltaBadge value={delta} />
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {r.department_name || ""}
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow
                            className="bg-muted/20"
                            data-testid={`eff-breakdown-${rowKey}`}
                          >
                            <TableCell colSpan={8} className="py-3">
                              <TxTypeBreakdown byType={r.by_type} total={r.total_transactions} />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
